/**
 * Shared session cookie utility for cross-product SSO.
 *
 * Both CAM and OS frontends store the Cognito access token in a cookie
 * on `AIBUILD_DOMAIN`, enabling instant SSO when the user switches products.
 *
 * Cookie names are environment-scoped via {@link COOKIE_SUFFIX} so that
 * test / prod never collide when a developer has multiple
 * environments open in the same browser. Prod keeps the bare names
 * (`aibuild_token`, `aibuild_refresh`) for backward compatibility;
 * non-prod builds append a suffix (e.g. `aibuild_token_test`).
 *
 * On localhost the cookie is shared across ports automatically (no domain needed).
 */

import { AIBUILD_DOMAIN, COOKIE_SUFFIX } from '@constants/index';

const COOKIE_NAME = `aibuild_token${COOKIE_SUFFIX}`;
const REFRESH_COOKIE_NAME = `aibuild_refresh${COOKIE_SUFFIX}`;
const REFRESH_MAX_AGE = 30 * 24 * 3600; // 30 days — matches Cognito refresh token validity

function getCookieDomain(): string | undefined {
  const hostname = globalThis.location.hostname;
  if (
    hostname === 'localhost' ||
    hostname.startsWith('127.') ||
    hostname === '::1' ||
    hostname === '[::1]'
  ) {
    return undefined;
  }
  return AIBUILD_DOMAIN;
}

/**
 * Store the Cognito access token in the shared SSO cookie.
 * Call this on login success and after token refresh.
 */
export function setSessionCookie(accessToken: string, maxAgeSeconds = 28800): void {
  const domain = getCookieDomain();
  const parts = [
    `${COOKIE_NAME}=${encodeURIComponent(accessToken)}`,
    'Path=/',
    `Max-Age=${maxAgeSeconds}`,
    'SameSite=Lax',
  ];
  if (domain) {
    parts.push(`Domain=${domain}`);
  }
  if (globalThis.location.protocol === 'https:') {
    parts.push('Secure');
  }
  document.cookie = parts.join('; ');
}

/**
 * Read the Cognito access token from the shared SSO cookie.
 * Returns null if the cookie is not present or expired.
 */
export function getSessionCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, ...valueParts] = cookie.trim().split('=');
    if (name === COOKIE_NAME) {
      const value = valueParts.join('=');
      return value ? decodeURIComponent(value) : null;
    }
  }
  return null;
}

/**
 * Remove the shared SSO cookie. Call this on logout.
 */
export function clearSessionCookie(): void {
  const domain = getCookieDomain();
  const parts = [
    `${COOKIE_NAME}=`,
    'Path=/',
    'Max-Age=0',
    'SameSite=Lax',
  ];
  if (domain) {
    parts.push(`Domain=${domain}`);
  }
  document.cookie = parts.join('; ');
}

/**
 * Store the Cognito refresh token in a shared cookie for cross-product SSO.
 * The SSO-receiving app reads this to get a refresh token it can use
 * with the shared Cognito client credentials.
 */
export function setRefreshCookie(refreshToken: string): void {
  const domain = getCookieDomain();
  const parts = [
    `${REFRESH_COOKIE_NAME}=${encodeURIComponent(refreshToken)}`,
    'Path=/',
    `Max-Age=${REFRESH_MAX_AGE}`,
    'SameSite=Lax',
  ];
  if (domain) {
    parts.push(`Domain=${domain}`);
  }
  if (globalThis.location.protocol === 'https:') {
    parts.push('Secure');
  }
  document.cookie = parts.join('; ');
}

/**
 * Read the Cognito refresh token from the shared cookie.
 * Returns null if the cookie is not present or expired.
 */
export function getRefreshCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, ...valueParts] = cookie.trim().split('=');
    if (name === REFRESH_COOKIE_NAME) {
      const value = valueParts.join('=');
      return value ? decodeURIComponent(value) : null;
    }
  }
  return null;
}

/**
 * Extract the Cognito username (`username` or `sub` claim) from a JWT.
 *
 * Cognito's REFRESH_TOKEN_AUTH requires SECRET_HASH to be computed with
 * the actual pool username (typically the `sub` UUID), NOT the email.
 * This helper decodes the JWT payload without verification — the token
 * may be expired but the claims are still readable.
 */
export function extractCognitoUsername(jwt: string): string | null {
  try {
    const [, payloadB64Url] = jwt.split('.');
    if (!payloadB64Url) return null;
    const b64 = payloadB64Url.replaceAll('-', '+').replaceAll('_', '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const json = new TextDecoder().decode(
      Uint8Array.from(atob(padded), (c) => c.codePointAt(0)!),
    );
    const payload = JSON.parse(json);
    return payload.username || payload.sub || null;
  } catch {
    return null;
  }
}

/**
 * Remove the shared refresh token cookie. Call this on logout.
 */
export function clearRefreshCookie(): void {
  const domain = getCookieDomain();
  const parts = [
    `${REFRESH_COOKIE_NAME}=`,
    'Path=/',
    'Max-Age=0',
    'SameSite=Lax',
  ];
  if (domain) {
    parts.push(`Domain=${domain}`);
  }
  document.cookie = parts.join('; ');
}
